import type { Placement } from "@floating-ui/react";
import React, { useCallback, useState } from "react";

import { Popper } from "@/components/atoms/Popper";
import { SelectContext, type SelectSize } from "./Select.hooks";
import { SelectOption } from "./SelectOption";
import { SelectOptions } from "./SelectOptions";
import { SelectTrigger } from "./SelectTrigger";

export interface SelectProps<T> {
  /**
   * The currently selected item — the full object from `data`, or null/undefined
   * when nothing is selected.
   */
  value: T | null | undefined;
  /** Called with the full option object when the user makes a selection. */
  onChange: (item: T) => void;
  /** The full option dataset. Passed through to `<Select.Options>` via context. */
  data: T[];
  /** Returns a stable unique key for each option (used for selection matching). */
  getOptionKey: (item: T) => string | number;
  /**
   * Compositional children — must include at least a `<Select.Trigger>` and a
   * `<Select.Options>`. Any other children are rendered as-is.
   *
   * ```tsx
   * <Select value={v} onChange={setV} data={items} getOptionKey={(i) => i.id}>
   *   <Select.Trigger placeholder="Pick one">
   *     {v?.label}
   *   </Select.Trigger>
   *   <Select.Options>
   *     {(item) => <Select.Option value={item}>{item.label}</Select.Option>}
   *   </Select.Options>
   * </Select>
   * ```
   */
  children: React.ReactNode;
  /**
   * Optional external anchor (e.g. an ``IconButton`` inside a ``ButtonGroup``).
   * When set, ``<Select.Trigger>`` is not required.
   */
  anchor?: React.ReactNode;
  /** Min width of the floating panel (defaults to "200px"). */
  popperMinWidth?: React.CSSProperties["width"];
  /** Max height of the floating panel (defaults to "280px"). */
  popperMaxHeight?: React.CSSProperties["height"];
  /** Size applied consistently to the trigger button and the options list (default "md"). */
  size?: SelectSize;
  /** Floating-ui placement for the options panel (default "bottom-start"). */
  placement?: Placement;
  /** Controlled open state for composite anchors (e.g. a split ``ButtonGroup``). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When false, only explicit triggers (e.g. chevron ``onClick``) open the panel. */
  popperClickable?: boolean;
  /** The root wrapper's own style. */
  style?: React.CSSProperties;
}

/**
 * A composable select / dropdown built on Popper + List.
 *
 * Sub-components are attached as static properties:
 *   `Select.Trigger`  — the clickable anchor button
 *   `Select.Options`  — the floating options panel (renders a List internally)
 *   `Select.Option`   — a single option item
 */
export function Select<T>({
  value,
  onChange,
  data,
  getOptionKey,
  children,
  anchor,
  size = "md",
  placement = "bottom-start",
  popperMinWidth = "200px",
  popperMaxHeight = "280px",
  open,
  onOpenChange,
  popperClickable = true,
  style,
}: SelectProps<T>) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setIsOpen = useCallback(
    (next: boolean) => {
      if (open === undefined) {
        setInternalOpen(next);
      }
      onOpenChange?.(next);
    },
    [onOpenChange, open],
  );

  const onSelect = useCallback(
    (item: T) => {
      onChange(item);
      setIsOpen(false);
    },
    [onChange, setIsOpen],
  );

  // Split children into trigger vs. options panel. Any element whose type is
  // SelectTrigger becomes the Popper anchor; SelectOptions goes inside the
  // floating panel. Other children are ignored (not rendered).
  const childArray = React.Children.toArray(children);
  const triggerChild = childArray.find(
    (c) => React.isValidElement(c) && c.type === SelectTrigger,
  ) as React.ReactElement | undefined;
  const optionsChild = childArray.find(
    (c) => React.isValidElement(c) && c.type === SelectOptions,
  ) as React.ReactElement | undefined;

  const popperAnchor = anchor ?? triggerChild;

  const contextValue = {
    value,
    onSelect,
    data,
    getOptionKey,
    isOpen,
    setIsOpen,
    size,
  };

  return (
    <SelectContext.Provider value={contextValue}>
      <Popper
        anchor={popperAnchor}
        isOpenState={[isOpen, setIsOpen]}
        placement={placement}
        isClickable={popperClickable}
        style={{
          width: anchor ? "auto" : "100%",
          ...style,
        }}
        floatingStyle={{
          minWidth: popperMinWidth as string,
          maxHeight: popperMaxHeight as string,
          overflowY: "auto",
        }}
      >
        {optionsChild}
      </Popper>
    </SelectContext.Provider>
  );
}

// Attach sub-components as static properties
Select.Trigger = SelectTrigger;
Select.Options = SelectOptions;
Select.Option = SelectOption;

export { SelectTrigger, SelectOptions, SelectOption };
export { useSelectContext } from "./Select.hooks";
